import { AuthTokenPayload } from "@vertesia/common"
import { jwtDecode } from "jwt-decode"

import { CONFIGURATION } from "@env/variables"

import { firebaseAuth, getFirebaseAuthToken } from "@plugins/session/auth/firebase"

import { LastSelectedAccountId_KEY, LastSelectedProjectId_KEY } from "@session/UserSession"

let AUTH_TOKEN_RAW: string | undefined
let AUTH_TOKEN: AuthTokenPayload | undefined

interface ComposableTokenResponse {
    rawToken: string
    token: AuthTokenPayload
    error: boolean
    message?: string
}

export async function fetchComposableToken(getIdToken: () => Promise<string | null | undefined>, accountId?: string, projectId?: string, ttl?: number): Promise<string> {
    const idToken = await getIdToken()
    if (!idToken) {
        throw new Error("No id token found")
    }

    const url = new URL(CONFIGURATION.endpoints.studio + "/auth/token")
    if (accountId) {
        url.searchParams.set("accountId", accountId)
    }
    if (projectId) {
        url.searchParams.set("projectId", projectId)
    }
    if (ttl) {
        url.searchParams.set("ttl", String(ttl))
    }

    const res = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
    }).catch((err) => {
        localStorage.removeItem(LastSelectedAccountId_KEY)
        localStorage.removeItem(LastSelectedProjectId_KEY)
        throw new Error("Connection Error: Failed to get composable token. Message:\n" + err.message)
    })

    if (idToken && res?.status === 412) {
        const idTokenDecoded = jwtDecode(idToken) as any
        if (!idTokenDecoded?.email) {
            throw new Error("No email found in id token")
        }
        throw new UserNotFoundError("User not found", idTokenDecoded.email)
    }

    if (!res?.ok) {
        throw new Error("Failed to get composable token")
    }

    const { token } = await res.json().catch((err) => {
        console.error("Failed to parse composable token", err)
    })

    return token
}

export async function fetchComposableTokenFromFirebaseToken(accountId?: string, projectId?: string, ttl?: number) {
    return fetchComposableToken(getFirebaseAuthToken, accountId, projectId, ttl)
}

export async function getComposableToken(accountId?: string, projectId?: string, initToken?: string, forceRefresh = false): Promise<ComposableTokenResponse> {
    const selectedAccount = accountId ?? localStorage.getItem(LastSelectedAccountId_KEY) ?? undefined
    const selectedProject = projectId ?? localStorage.getItem(LastSelectedProjectId_KEY) ?? undefined

    if (!forceRefresh && AUTH_TOKEN_RAW && AUTH_TOKEN && AUTH_TOKEN.exp > Date.now() / 1000 + 300) {
        return { rawToken: AUTH_TOKEN_RAW, token: AUTH_TOKEN, error: false }
    }

    if (firebaseAuth.currentUser) {
        AUTH_TOKEN_RAW = await fetchComposableTokenFromFirebaseToken(selectedAccount, selectedProject)
    } else if (initToken || AUTH_TOKEN_RAW) {
        AUTH_TOKEN_RAW = initToken ?? (await fetchComposableToken(() => Promise.resolve(initToken ?? AUTH_TOKEN_RAW), selectedAccount, selectedProject))
    }

    if (!AUTH_TOKEN_RAW) {
        throw new Error("Cannnot acquire a composable token")
    }

    AUTH_TOKEN = jwtDecode(AUTH_TOKEN_RAW)

    if (!AUTH_TOKEN?.exp || !AUTH_TOKEN_RAW) {
        throw new Error("Invalid composable token")
    }

    return { rawToken: AUTH_TOKEN_RAW, token: AUTH_TOKEN, error: false }
}

export class UserNotFoundError extends Error {
    email: string
    constructor(message: string, email: string) {
        super(message)
        this.name = "UserNotFoundError"
        this.email = email
    }
}
